import React, { useEffect, useState } from "react";
import {
  Drawer,
  Row,
  Col,
  Button,
  List,
  Card,
  notification,
  Divider,
  Tooltip,
  Radio,
  RadioChangeEvent,
} from "antd";
import moment from "moment";
import styles from "./ScheduleGrocery.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import drawerStyles from "../../DashBoard/TripDetails/TripDetailsDrawer.module.scss";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import dividerStyles from "../../Shared/Styles/DividerStyles.module.scss";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import { usePlanDetailsCtx } from "../../../context/PlanDetailsContext";
import Spinner from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { getGroceryDeliveryAvailability } from "../../../services/GroceryService";
import { useGroceryCtx } from "../../../context/GroceryContext";
import infoIcon from "../../../assets/icons/infoCircleOutlined.svg";
import { DeliverySchedule } from "../../../types/GroceryTypes";

type Props = {
  isVisible: boolean;
  setIsVisible: Function;
  setIsOpenDeliveryAddressDrawer: Function;
};

type DateList = {
  day: string;
  month: string;
  date: string;
  disabled: boolean;
  fullDate: string;
};

function ScheduleGroceryDrawer({
  isVisible,
  setIsVisible,
  setIsOpenDeliveryAddressDrawer,
}: Props) {
  const [availability, setAvailability] = useState<{
    dates: DateList[];
    windows: string[];
  }>();
  const [isLoadingAvailability, setIsLoadingAvailability] = useState<boolean>(
    false
  );
  const [isGroceryAvailable, setIsGroceryAvailable] = useState<boolean>(true);

  const {
    deliveryAddressCtx,
    isDeliveryScheduledCtx,
    selectedDeliveryScheduleCtx,
  } = useGroceryCtx();
  const [deliveryAddress] = deliveryAddressCtx;
  const [, setIsDeliveryScheduled] = isDeliveryScheduledCtx;
  const [
    selectedDeliverySchedule,
    setSelectedDeliverySchedule,
  ] = selectedDeliveryScheduleCtx;

  const { isWandaConcierge } = usePersonSummaryCtx();
  const { tripDetailsInfo } = usePlanDetailsCtx();

  useEffect(() => {
    getAvailableDatesAndTime();
  }, [deliveryAddress?.zip]);

  async function getAvailableDatesAndTime() {
    try {
      setIsLoadingAvailability(true);
      if (deliveryAddress.organizationId) {
        const data = await getGroceryDeliveryAvailability(
          deliveryAddress.organizationId
        );

        if (data) {
          const processedDates = data.availableDates.map(
            (
              availableDate: { date: string; isActive: boolean },
              index: number
            ) => {
              const date = moment.utc(availableDate.date);
              let day = date.format("dddd");
              let disabled = !availableDate.isActive;

              if (index === 0) {
                day = "Today";
                disabled = !isWandaConcierge || disabled;
              } else if (index === 1) {
                day = "Tomorrow";
                disabled = !isWandaConcierge || disabled;
              }

              return {
                day,
                month: date.format("MMM"),
                date: date.format("DD"),
                disabled,
                fullDate: availableDate.date,
              };
            }
          );

          setAvailability({
            dates: processedDates,
            windows: data.availableWindow,
          });

          const defaultSelectedDate = processedDates.find(
            (availableDate: DateList) => !availableDate.disabled
          );
          setSelectedDeliverySchedule((prev: DeliverySchedule) => ({
            ...prev,
            date: defaultSelectedDate?.fullDate || "",
            window: data.availableWindow[0],
          }));
          setIsGroceryAvailable(true);
        } else {
          setAvailability(undefined);
          setIsGroceryAvailable(false);
        }
      }
    } catch {
      notification.error({
        message: "Failed to Fetch Delivery Availability",
      });
    } finally {
      setIsLoadingAvailability(false);
    }
  }

  function handleDateSelection(date: string, isDisabled: boolean) {
    !isDisabled &&
      setSelectedDeliverySchedule((prev: DeliverySchedule) => ({
        ...prev,
        date: date,
      }));
  }

  function handleCloseDrawer() {
    setIsVisible(false);
  }

  function handleContinueShopping() {
    setIsDeliveryScheduled(true);
    setIsVisible(false);
  }

  function onChangeWindow(e: RadioChangeEvent): void {
    setSelectedDeliverySchedule((prev: DeliverySchedule) => ({
      ...prev,
      window: e.target.value,
    }));
  }

  function handleChangeDeliveryAddress(): void {
    setIsOpenDeliveryAddressDrawer(true);
  }

  return (
    <>
      <Drawer
        title={
          <Row className={textStyles.normalText20}>
            Delivery: Time and Place
          </Row>
        }
        placement="right"
        className={drawerStyles.drawerContainer}
        visible={isVisible}
        closable={false}
      >
        <Divider className={dividerStyles.solidBrandingDivider} />

        <Row className={styles.scheduleDeliveryBody} justify="space-between">
          {isLoadingAvailability ? (
            <Spinner />
          ) : (
            isGroceryAvailable && (
              <>
                <Col className={textStyles.normalText20}>Date</Col>
                <Col>
                  <Tooltip
                    title={
                      <Row>
                        <Col span={24}>Please call us at</Col>
                        <Col span={24}>
                          {tripDetailsInfo.wandaCustomerCareNumber} for help.
                        </Col>
                      </Row>
                    }
                    placement="left"
                    overlayClassName={styles.infoTooltip}
                  >
                    <img src={infoIcon} alt="info" />
                  </Tooltip>
                </Col>
                <Col span={24} className={textStyles.secondaryNormalText14}>
                  The delivery order must be placed at least 2 business days
                  before the delivery date.
                </Col>
                <Col span={24} className={styles.calendarContainer}>
                  <List
                    grid={{ gutter: 16, column: 4 }}
                    dataSource={availability?.dates}
                    renderItem={(date: DateList, index) => (
                      <List.Item key={date.fullDate}>
                        <Card
                          className={`${styles.dateCard} ${date.disabled &&
                            styles.disabledDateCard} ${selectedDeliverySchedule.date ===
                            date.fullDate && styles.selectedDateCard}`}
                          onClick={() =>
                            handleDateSelection(date.fullDate, date.disabled)
                          }
                        >
                          <Row
                            justify="center"
                            className={textStyles.secondaryNormalText12}
                          >
                            {date.day}
                          </Row>
                          <Row
                            className={textStyles.boldText16}
                            justify="center"
                            gutter={[6, 0]}
                          >
                            <Col>{date.month}</Col>
                            <Col>{date.date}</Col>
                          </Row>
                        </Card>
                      </List.Item>
                    )}
                  />
                </Col>

                <Col span={24} className={textStyles.normalText20}>
                  Available hours
                </Col>
                <Col span={24} className={textStyles.secondaryNormalText14}>
                  Select from the 2 hour windows available.
                </Col>
                <Col span={24} className={styles.windowsContainer}>
                  <Radio.Group
                    onChange={onChangeWindow}
                    value={selectedDeliverySchedule.window}
                  >
                    {availability?.windows.map((window) => (
                      <Radio
                        key={window}
                        value={window}
                        className={`${window ===
                          selectedDeliverySchedule.window &&
                          styles.selectedOption}`}
                      >
                        {window}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Col>

                <Divider className={dividerStyles.solidFadedDivider} />
              </>
            )
          )}

          <Col span={24}>
            <Row justify="space-between" gutter={[0, 4]}>
              <Col className={textStyles.normalText20}>Delivery address</Col>
              <Col>
                <Button
                  type="link"
                  className={`${buttonStyles.linkBtn} ${textStyles.normalText14}`}
                  onClick={handleChangeDeliveryAddress}
                >
                  Change
                </Button>
              </Col>
              <Col span={24} />
            </Row>

            <Row className={textStyles.secondaryNormalText14}>
              <Col span={24}>{deliveryAddress.address1}</Col>
              <Col span={24}>{deliveryAddress.address2}</Col>
              <Col span={24}>
                {deliveryAddress.city}, {deliveryAddress.state}{" "}
                {deliveryAddress.zip}
              </Col>
            </Row>
          </Col>

          {!isGroceryAvailable && (
            <Col
              span={24}
              className={`${textStyles.errorNormalText14} ${styles.marginUp32}`}
            >
              We're sorry, but Grocery isn't available in your area yet.
            </Col>
          )}
        </Row>

        <Row
          justify="center"
          className={buttonStyles.drawerFooterBtnsContainer}
          gutter={[16, 16]}
        >
          <Col flex={1}>
            <Button
              className={`${buttonStyles.defaultWhiteBtn} ${textStyles.normalText16}`}
              onClick={handleCloseDrawer}
            >
              Cancel
            </Button>
          </Col>
          <Col flex={3}>
            <Button
              className={`${styles.continueShoppingBtn} ${
                buttonStyles.primaryBlueBtn
              } ${!isGroceryAvailable && buttonStyles.disabledBtn}`}
              onClick={handleContinueShopping}
              disabled={!isGroceryAvailable}
            >
              Continue shopping
            </Button>
          </Col>
        </Row>
      </Drawer>
    </>
  );
}

export default ScheduleGroceryDrawer;
